import type { ReactNode, SVGProps } from 'react';

type GlyphProps = SVGProps<SVGSVGElement>;

function Glyph({ children, ...props }: GlyphProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="h-5 w-5"
      {...props}
    >
      {children}
    </svg>
  );
}

export function HeaderIconTile({
  color,
  children,
  size = 'md',
}: {
  color: string;
  children: ReactNode;
  size?: 'sm' | 'md';
}) {
  const box = size === 'sm' ? 'h-9 w-9 rounded-xl' : 'h-11 w-11 rounded-2xl';
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center text-white shadow-sm ${box}`}
      style={{ backgroundColor: color }}
      aria-hidden
    >
      {children}
    </span>
  );
}

export const HEADER_NAVY = '#0d3f67';
export const HEADER_TEAL = '#00aaa4';
export const HEADER_TEAL_INK = '#04221f';
export const HEADER_ORANGE = '#ff9f0a';
export const HEADER_GREEN = '#248a3d';
export const HEADER_INDIGO = '#5e5ce6';
export const WASC_NAVY = '#001840';
export const WASC_TEAL = '#1e8a7f';

export function HomeGlyph() {
  return (
    <Glyph>
      <path d="M4 10.5 12 4l8 6.5" />
      <path d="M6.5 9.8V20h11V9.8" />
    </Glyph>
  );
}

export function SessionsGlyph() {
  return (
    <Glyph>
      <rect x="4" y="5" width="16" height="15" rx="2" />
      <path d="M8 3.5v3.5M16 3.5v3.5M4 10h16" />
    </Glyph>
  );
}

export function PeopleGlyph() {
  return (
    <Glyph>
      <circle cx="9" cy="9" r="2.4" />
      <circle cx="16" cy="10" r="2" />
      <path d="M4.5 18.5c.6-2.4 2.4-3.6 4.5-3.6s3.9 1.2 4.5 3.6" />
      <path d="M13.5 15.2c1.4-.5 2.8-.2 4 .8 1 .8 1.6 1.7 1.8 2.5" />
    </Glyph>
  );
}

export function MediaGlyph() {
  return (
    <Glyph>
      <path d="M5 5.5h10.5a2 2 0 0 1 2 2V19L14 16.2H5a2 2 0 0 1-2-2v-6.7a2 2 0 0 1 2-2Z" />
      <path d="M8 9.5h6M8 12.5h4" />
    </Glyph>
  );
}

export function FeaturesGlyph() {
  return (
    <Glyph>
      <rect x="4" y="4" width="7" height="7" rx="1.5" />
      <rect x="13" y="4" width="7" height="7" rx="1.5" />
      <rect x="4" y="13" width="7" height="7" rx="1.5" />
      <rect x="13" y="13" width="7" height="7" rx="1.5" />
    </Glyph>
  );
}

export function MicGlyph() {
  return (
    <Glyph>
      <rect x="9" y="3.5" width="6" height="10" rx="3" />
      <path d="M7 11a5 5 0 0 0 10 0M12 16v4.5" />
    </Glyph>
  );
}

export function PricingGlyph() {
  return (
    <Glyph>
      <path d="M12 3.5v17" />
      <path d="M16.2 7.2c0-1.6-1.8-2.7-4.2-2.7S7.8 5.6 7.8 7.2 9.6 9.6 12 10s4.2 1.2 4.2 2.8-1.8 2.7-4.2 2.7-4.2-1.1-4.2-2.7" />
    </Glyph>
  );
}

export function StoryGlyph() {
  return (
    <Glyph>
      <path d="M6 5.5h9.5A2.5 2.5 0 0 1 18 8v11H8.2A2.2 2.2 0 0 0 6 21.2V5.5Z" />
      <path d="M6 5.5A2.2 2.2 0 0 0 3.8 7.7V18" />
    </Glyph>
  );
}

export function FlagGlyph() {
  return (
    <Glyph>
      <path d="M6 4v16" />
      <path d="M6 5h11l-2 4 2 4H6" />
    </Glyph>
  );
}

export function MenuGlyph() {
  return (
    <Glyph>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </Glyph>
  );
}

export function CloseGlyph() {
  return (
    <Glyph>
      <path d="M6 6l12 12M18 6 6 18" />
    </Glyph>
  );
}

export function MenuButton({
  open,
  onClick,
  controls,
  color,
}: {
  open: boolean;
  onClick: () => void;
  controls: string;
  color: string;
}) {
  return (
    <button
      type="button"
      className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl text-white shadow-sm xl:hidden"
      style={{ backgroundColor: color }}
      aria-expanded={open}
      aria-controls={controls}
      onClick={onClick}
    >
      <span className="sr-only">{open ? 'Close menu' : 'Open menu'}</span>
      {open ? <CloseGlyph /> : <MenuGlyph />}
    </button>
  );
}
